// Local-timezone week grid helpers for the Logistics calendar mode.
// Mirrors the browser-local calendar-day pattern in `clientLocalDay.ts`.

import type { LogiRoute, LogiRouteState } from "@/lib/logistics/types";

export type LogisticsCalendarDayRollup = {
  date: string;
  scheduled: number;
  in_transit: number;
  delivered: number;
  routes: LogiRoute[];
};

export function localCalendarDayFromDate(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

export function parseLocalCalendarDay(date: string): Date | null {
  const m = date.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!m) return null;
  const year = Number(m[1]);
  const month0 = Number(m[2]) - 1;
  const day = Number(m[3]);
  if (!Number.isFinite(year) || !Number.isFinite(month0) || !Number.isFinite(day)) return null;
  return new Date(year, month0, day);
}

/** Sunday-start week containing `anchor` (US dispatch convention). */
export function weekStartDate(anchor: Date): Date {
  const d = new Date(anchor.getFullYear(), anchor.getMonth(), anchor.getDate());
  d.setDate(d.getDate() - d.getDay());
  return d;
}

export function weekDayKeys(anchor: Date): string[] {
  const start = weekStartDate(anchor);
  const keys: string[] = [];
  for (let i = 0; i < 7; i++) {
    const d = new Date(start);
    d.setDate(start.getDate() + i);
    keys.push(localCalendarDayFromDate(d));
  }
  return keys;
}

export function localDayIsoBounds(date: string): { from: string; to: string } | null {
  const d = parseLocalCalendarDay(date);
  if (!d) return null;
  const start = new Date(d.getFullYear(), d.getMonth(), d.getDate(), 0, 0, 0, 0);
  const end = new Date(d.getFullYear(), d.getMonth(), d.getDate(), 23, 59, 59, 999);
  return { from: start.toISOString(), to: end.toISOString() };
}

export function localWeekIsoBounds(anchor: Date): { from: string; to: string } {
  const keys = weekDayKeys(anchor);
  const first = localDayIsoBounds(keys[0]!)!;
  const last = localDayIsoBounds(keys[keys.length - 1]!)!;
  return { from: first.from, to: last.to };
}

function rollupCount(routes: LogiRoute[], state: LogiRouteState): number {
  return routes.filter((r) => r.route_state === state).length;
}

export function buildWeekRollups(
  weekKeys: string[],
  routes: LogiRoute[]
): LogisticsCalendarDayRollup[] {
  const byDay = new Map<string, LogiRoute[]>();
  for (const key of weekKeys) byDay.set(key, []);
  for (const route of routes) {
    if (!route.scheduled_for) continue;
    const key = localCalendarDayFromDate(new Date(route.scheduled_for));
    if (!byDay.has(key)) continue;
    byDay.get(key)!.push(route);
  }
  return weekKeys.map((date) => {
    const dayRoutes = [...(byDay.get(date) ?? [])].sort((a, b) => {
      const at = a.scheduled_for ? Date.parse(a.scheduled_for) : 0;
      const bt = b.scheduled_for ? Date.parse(b.scheduled_for) : 0;
      return at - bt;
    });
    return {
      date,
      scheduled: rollupCount(dayRoutes, "scheduled"),
      in_transit: rollupCount(dayRoutes, "in_transit"),
      delivered: rollupCount(dayRoutes, "delivered"),
      routes: dayRoutes,
    };
  });
}

export function formatWeekRangeLabel(weekKeys: string[]): string {
  if (weekKeys.length === 0) return "";
  const first = parseLocalCalendarDay(weekKeys[0]!);
  const last = parseLocalCalendarDay(weekKeys[weekKeys.length - 1]!);
  if (!first || !last) return "";
  const fmt: Intl.DateTimeFormatOptions = { month: "short", day: "numeric" };
  const y = first.getFullYear() === last.getFullYear() ? undefined : "numeric";
  return `${first.toLocaleDateString(undefined, { ...fmt, year: y })} – ${last.toLocaleDateString(undefined, { ...fmt, year: "numeric" })}`;
}
